<template>
  <v-container fluid class="pa-0 pa-md-5">
    <v-row class="mb-5">
      <v-col cols="12">
        <v-card dark class="pa-4 rounded-lg d-flex align-center justify-space-between" outlined elevation="2" style="border-left: 5px solid #4caf50">
          <div>
            <div class="text-overline mb-1 grey--text text--darken-1">QR-Rechnungen ({{ statusFilter === "open" ? "Offen" : "Bezahlt" }})</div>
            <div class="d-flex align-center">
              <div class="text-h4 success--text font-weight-bold mr-2">{{ totalContractsCount }}</div>
              <v-chip x-small color="success" outlined v-if="loading">lade...</v-chip>
            </div>
          </div>
          <v-icon style="opacity: 0.1; font-size: 60px">mdi-qrcode-scan</v-icon>
        </v-card>
      </v-col>
    </v-row>

    <v-card dark elevation="3" class="mb-6 rounded-lg">
      <v-card-text class="pa-4">
        <v-row align="center" dense>
          <v-col cols="12" sm="auto" class="mb-2 mb-sm-0">
            <v-btn-toggle v-model="statusFilter" mandatory class="custom-toggle" color="success">
              <v-btn value="open">Offen</v-btn>
              <v-btn value="paid">Bezahlt</v-btn>
            </v-btn-toggle>
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-select
              :items="studios"
              label="Studios filtern"
              v-model="studioSelect"
              item-text="name"
              item-value="_id"
              multiple
              outlined
              dense
              hide-details
              small-chips
              deletable-chips>
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text text-caption font-weight-bold"> {{ studioSelect.length }} Studios </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="auto" class="d-flex justify-end align-center ml-auto">
            <v-file-input
              v-model="bankFile"
              hide-details
              dense
              outlined
              prepend-icon=""
              prepend-inner-icon="mdi-bank-transfer"
              label="Bank-Datei (.csv/.xml) hochladen"
              class="mr-4"
              style="max-width: 300px"
              @change="handleFileUpload"
              :loading="uploadingFile"></v-file-input>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="grey darken-1" @click="selectAllStudios" v-bind="attrs" v-on="on" class="mr-2">
                  <v-icon>{{
                    studioSelect.length === studios.length ? "mdi-checkbox-multiple-outline" : "mdi-checkbox-multiple-blank-outline"
                  }}</v-icon>
                </v-btn>
              </template>
              <span>Alle Studios an/abwählen</span>
            </v-tooltip>

            <v-btn icon color="success" @click="fetchData(true)" :loading="loading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card dark elevation="0" class="mx-auto transparent" color="transparent">
      <v-row v-if="loading && page === 1" class="justify-center py-10">
        <v-progress-circular indeterminate color="success" size="64"></v-progress-circular>
      </v-row>

      <v-row v-else-if="qrContracts.length === 0" class="justify-center py-10">
        <v-col cols="12" class="text-center grey--text text--lighten-1">
          <v-icon size="80" color="grey darken-2">mdi-check-all</v-icon>
          <div class="text-h6 mt-4">Keine {{ statusFilter === "open" ? "offenen" : "bezahlten" }} Rechnungen gefunden</div>
        </v-col>
      </v-row>

      <div v-else>
        <v-expansion-panels v-model="userContractPanel" dark>
          <v-expansion-panel v-for="(user, i) of qrContracts" :key="user._id" class="mb-3 rounded-lg elevation-4">
            <v-expansion-panel-header :color="'#424242'">
              <template v-slot:default>
                <v-row no-gutters align="center">
                  <v-col cols="12" sm="4" class="d-flex align-center">
                    <span class="text-h6 font-weight-bold mr-2">{{ user.vorname }} {{ user.nachname }}</span>
                  </v-col>
                  <v-col cols="6" sm="3" class="text-caption grey--text text--lighten-2 d-flex align-center">
                    <v-icon x-small left color="grey lighten-2">mdi-map-marker</v-icon>
                    <span class="text-truncate">{{ getStudiosOfContracts(user.contracts) }}</span>
                  </v-col>
                  <v-col cols="6" sm="5" class="text-caption white--text d-flex align-center justify-end">
                    <v-chip small :color="statusFilter === 'open' ? 'orange' : 'success'" outlined> {{ user.contracts.length }} Rechnung(en) </v-chip>
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="grey darken-4 pt-4">
              <v-expansion-panels v-model="openContractPanel" accordion flat>
                <v-expansion-panel v-for="(contract, j) of user.contracts" :key="contract._id" class="transparent">
                  <v-expansion-panel-header class="pl-0 py-2">
                    <div class="d-flex align-center" style="width: 100%">
                      <v-icon small class="mr-3" :color="statusFilter === 'open' ? 'orange' : 'success'">
                        {{ statusFilter === "open" ? "mdi-clock-outline" : "mdi-check-circle-outline" }}
                      </v-icon>
                      <div class="d-flex flex-column mr-auto">
                        <span class="subtitle-1 font-weight-medium">{{ getNameOfContract(contract.contractId) }}</span>
                        <span class="text-caption grey--text font-monospace">Ref: {{ formatQRReference(contract.qrReferenceNumber) }}</span>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-row dense>
                      <v-col cols="12" md="6" class="d-flex flex-column justify-center align-center">
                        <v-btn
                          large
                          color="primary"
                          class="mb-3"
                          @click="printUserContract(user._id, contract.index, getNameOfContract(contract.contractId))">
                          <v-icon left>mdi-file-document</v-icon> Vertrag Herunterladen
                        </v-btn>

                        <v-btn
                          v-if="contract.qrReferenceNumber"
                          large
                          outlined
                          color="success"
                          class="mb-3"
                          @click="downloadQRInvoice(user._id, contract.index, user.nachname)">
                          <v-icon left>mdi-receipt</v-icon> QR-Rechnung Herunterladen
                        </v-btn>

                        <v-btn
                          v-if="statusFilter === 'open' && contract.qrReferenceNumber"
                          outlined
                          color="orange"
                          @click="markAsPaid(user._id, contract.index)">
                          <v-icon left>mdi-cash-check</v-icon> Manuell als bezahlt markieren
                        </v-btn>
                      </v-col>
                    </v-row>

                    <v-expand-transition>
                      <div v-if="openContractPanel === j" class="mt-4">
                        <object
                          v-if="!isApp && contractSauce"
                          width="100%"
                          height="400px"
                          :data="contractSauce"
                          class="rounded border-white"></object>
                      </div>
                    </v-expand-transition>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="text-center mt-6" v-if="page < totalPages">
          <v-btn :loading="loadingMore" @click="loadMore" color="success" rounded outlined large>
            <v-icon left>mdi-chevron-down</v-icon> Mehr laden ({{ qrContracts.length }} / {{ totalContractsCount }})
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import api from "../../../api"

export default {
  data() {
    return {
      qrContracts: [],
      studios: [],
      studioSelect: [],
      statusFilter: "open", // open oder paid
      bankFile: null,
      uploadingFile: false,

      page: 1,
      limit: 20,
      totalPages: 1,
      totalContractsCount: 0,

      loading: true,
      loadingMore: false,

      userContractPanel: null,
      openContractPanel: null,
      contractSauce: null,

      contractsTemplates: [],
    }
  },

  async created() {
    try {
      const [studios, userStudio] = await Promise.all([api.fetchStudios(), api.getUserStudio()])
      this.studios = studios
      if (userStudio && userStudio.studio) {
        this.studioSelect = [userStudio.studio._id]
      }
      this.contractsTemplates = await api.fetchContracts()
      this.fetchData(true)
    } catch (e) {
      console.error("Init Error", e)
    }
  },

  computed: {
    isApp() {
      return window.flutter_inappwebview || false
    },
  },

  methods: {
    async downloadQRInvoice(userId, index, nachname) {
      try {
        this.$toast.info("Rechnung wird generiert...")

        // Wir rufen nur noch die API auf und übergeben den Namen für die Datei
        await api.downloadQRInvoicePDF(userId, index, nachname)

        this.$toast.success("Download gestartet!")
      } catch (e) {
        console.error("PDF Download Error", e)
        this.$toast.error("Fehler beim Herunterladen der Rechnung")
      }
    },
    selectAllStudios() {
      if (this.studioSelect.length === this.studios.length) {
        this.studioSelect = []
      } else {
        this.studioSelect = this.studios.map((s) => s._id)
      }
    },
    async fetchData(reset = false) {
      if (reset) {
        this.page = 1
        this.loading = true
        this.qrContracts = []
      } else {
        this.loadingMore = true
      }

      try {
        const params = {
          page: this.page,
          limit: this.limit,
          studios: this.studioSelect,
          paymentStatus: this.statusFilter, // NEU
        }

        // TODO: In deiner api.js musst du fetchQRContracts() anlegen!
        const response = await api.fetchQRContracts(params)

        let newUsers = response?.users || []
        this.totalContractsCount = response?.total || 0
        this.totalPages = response?.pages || 1

        if (reset) {
          this.qrContracts = newUsers
        } else {
          this.qrContracts = [...this.qrContracts, ...newUsers]
        }
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Rechnungen")
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    loadMore() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchData(false)
      }
    },

    // Formatiert die Referenznummer schön (00 00000 00000...)
    formatQRReference(ref) {
      if (!ref) return ""
      return ref.toString().replace(/\B(?=(\d{5})+(?!\d))/g, " ")
    },

    async handleFileUpload(file) {
      if (!file) return
      this.uploadingFile = true
      try {
        // Hier kommt später euer FormData Upload an euer Backend
        let formData = new FormData()
        formData.append("bankfile", file)
        // await api.uploadBankFile(formData);

        this.$toast.success("Datei verarbeitet! Rechnungen aktualisiert.")
        this.bankFile = null
        this.fetchData(true) // Liste neu laden
      } catch (e) {
        this.$toast.error("Fehler beim Verarbeiten der Datei.")
      } finally {
        this.uploadingFile = false
      }
    },

    async markAsPaid(userId, contractIndex) {
      // Kleiner Fallback, falls man doch mal manuell abkassiert
      if (confirm("Diesen Vertrag manuell als bezahlt markieren?")) {
        try {
          // TODO: Api Call um status auf 'paid' zu setzen
          // await api.markQRContractPaid(userId, contractIndex);
          this.$toast.success("Als bezahlt markiert")
          this.fetchData(true)
        } catch (e) {
          this.$toast.error("Fehler beim Speichern")
        }
      }
    },

    getNameOfStudio(studioId) {
      let studio = this.studios.find((e) => e._id === studioId)
      return studio && studio.name ? studio.name : "Unbekannt"
    },

    getStudiosOfContracts(contracts) {
      if (!contracts) return ""
      let studios = contracts.map((e) => e.studio._id)
      studios = studios.map((e) => this.getNameOfStudio(e))
      return [...new Set(studios)].join(", ")
    },

    getNameOfContract(contractId) {
      let contract = this.contractsTemplates.find((e) => e._id === contractId)
      return contract?.internalTitle || "Vertrag"
    },

    printUserContract(userId, index, title) {
      const safeTitle = (title || "Vertrag").replace(/[\\/:"*?<>|]/g, "_")
      api.printUserContract(userId, index, safeTitle)
    },

    async loadPdfPreview(userId, contractIndex) {
      this.contractSauce = null
      try {
        let blob = await api.getIframeprintUserContract(userId, contractIndex, "Vorschau")
        this.contractSauce = URL.createObjectURL(blob)
      } catch (e) {
        this.$toast.error("Vorschau konnte nicht geladen werden")
      }
    },
  },

  watch: {
    studioSelect(val) {
      if (val.length > 0) this.fetchData(true)
    },
    statusFilter() {
      this.fetchData(true) // Lädt Liste neu beim Wechsel Offen/Bezahlt
    },
    async openContractPanel(val) {
      if (val !== undefined && this.userContractPanel !== undefined) {
        let user = this.qrContracts[this.userContractPanel]
        if (user && user.contracts && user.contracts[val]) {
          let contract = user.contracts[val]
          await this.loadPdfPreview(user._id, contract.index)
        }
      } else {
        this.contractSauce = null
      }
    },
    userContractPanel() {
      this.openContractPanel = undefined
      this.contractSauce = null
    },
  },
}
</script>

<style scoped>
.transparent {
  background-color: transparent !important;
}
.border-white {
  border: 1px solid rgba(255, 255, 255, 0.5);
}
.font-monospace {
  font-family: monospace;
  letter-spacing: 0.05em;
}
</style>
